import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';

declare global {
	interface Window {
		dataLayer: any[];
		gtag?: (...args: any[]) => void;
	}
}

export interface DataLayerEvent {
	event: string;
	[key: string]: any;
}

@Injectable({
	providedIn: 'root',
})
export class DataLayerService {
	private isInitialized = false;
	private eventQueue: DataLayerEvent[] = [];

	constructor(@Inject(PLATFORM_ID) private platformId: object) {
		this.initializeDataLayer();
	}

	private initializeDataLayer(): void {
		if (!isPlatformBrowser(this.platformId)) {
			return;
		}

		// Initialize dataLayer if it doesn't exist
		if (!window.dataLayer) {
			window.dataLayer = [];
		}

		this.isInitialized = true;
		this.flushEventQueue();
	}

	private flushEventQueue(): void {
		while (this.eventQueue.length > 0) {
			const event = this.eventQueue.shift();
			if (event) {
				this.pushEvent(event);
			}
		}
	}

	/**
	 * Push an event to the data layer
	 * @param event - Event name
	 * @param data - Additional data to send with the event
	 */
	pushToDataLayer(event: string, data: { [key: string]: any } = {}): void {
		const eventData: DataLayerEvent = { event, ...data };

		if (!this.isInitialized || !isPlatformBrowser(this.platformId)) {
			this.eventQueue.push(eventData);
			return;
		}

		this.pushEvent(eventData);
	}

	private pushEvent(eventData: DataLayerEvent): void {
		try {
			if (window.dataLayer) {
				window.dataLayer.push(eventData);
				console.debug('DataLayer event pushed:', eventData);
			} else {
				console.warn('DataLayer not available, queuing event:', eventData);
				this.eventQueue.push(eventData);
			}
		} catch (error) {
			console.error('Error pushing to dataLayer:', error, eventData);
		}
	}

	/**
	 * Push a page view event
	 */
	pushPageView(pagePath: string, pageTitle?: string): void {
		this.pushToDataLayer('page_view', {
			page_path: pagePath,
			page_title: pageTitle || document.title,
		});
	}

	/**
	 * Push a custom event with structured data
	 */
	pushCustomEvent(eventName: string, parameters: { [key: string]: any } = {}): void {
		this.pushToDataLayer(eventName, {
			event_category: 'custom',
			...parameters,
		});
	}

	/**
	 * Push an e-commerce event
	 */
	pushEcommerceEvent(eventName: string, ecommerceData: { [key: string]: any }): void {
		this.pushToDataLayer(eventName, {
			ecommerce: ecommerceData,
		});
	}

	/**
	 * Clear the data layer (useful for testing)
	 */
	clearDataLayer(): void {
		if (isPlatformBrowser(this.platformId) && window.dataLayer) {
			window.dataLayer.length = 0;
		}
	}

	/**
	 * Get current data layer contents (useful for debugging)
	 */
	getDataLayer(): any[] {
		if (isPlatformBrowser(this.platformId) && window.dataLayer) {
			return [...window.dataLayer];
		}
		return [];
	}
}
